import {html, css, CSSResultGroup, TemplateResult, svg} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {BaseComponent} from './base-component.js';

@customElement('il-dropdown-header')
export default class DropdownHeaderComponent extends BaseComponent {
  @property({type: String}) label: string = '';
  @property({type: String}) detail: string = '';
  @property({type: Boolean, reflect: true}) expanded: boolean = false;
  @property({type: Boolean}) truncateLabel: boolean = false;
  @property({type: Object}) onClick?: () => void;

  private chevronDown = svg`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 7.28114L10 14.3L17 7.28113L15.7055 6L10 11.7209L4.29449 6L3 7.28114Z" fill="currentColor"/></svg>`;
  private chevronUp = svg`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 13.0189L10 6L17 13.0189L15.7055 14.3L10 8.57911L4.29449 14.3L3 13.0189Z" fill="currentColor"/></svg>`;

  override render(): TemplateResult {
    return html`
      <button
        class="${this.baseCSSClass} il-dropdown-header"
        type="button"
        @click="${this._onClick}"
        data-test-iliad-dropdown-header
      >
        <div class="il-dropdown-header__left">
          <div class="il-dropdown-header__left__label ${this.truncateLabel ? 'il-dropdown-header__left__label--truncate' : ''}">
            <slot name="icon"></slot>
            <span class="il-dropdown-header__label ${this.truncateLabel ? 'il-dropdown-header__label--truncate' : ''}">${this.label}</span>
          </div>

          <span class="il-dropdown-header__chevron">
            ${this.expanded ? this.chevronUp : this.chevronDown}
          </span>
        </div>

        <div class="il-dropdown-header__right">
          ${this.detail ? html`<span class="il-dropdown-header__detail">${this.detail}</span>` : ''}
        </div>
      </button>
    `;
  }

  private _onClick(): void {
    this.dispatchEvent(new Event('header-click', {bubbles: true, composed: true}));
    this.onClick?.();
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      :host {
        display: block;
      }

      .il-dropdown-header {
        width: 100%;
        padding: 12px 16px;
        gap: 8px;
        background: var(--il-color-neutrals-0);
        border: 1px solid var(--il-color-neutrals-200);
        border-radius: 6px;
        cursor: pointer;
        outline: none;
        box-sizing: border-box;
      }

      .il-dropdown-header:hover {
        background-color: var(--il-color-neutrals-50);
      }

      .il-dropdown-header:focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-dropdown-header__left {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .il-dropdown-header__left__label {
        text-align: left;
      }

      .il-dropdown-header__left__label--truncate {
        min-width: 0;
      }

      .il-dropdown-header__label {
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-bold);
        color: var(--il-color-neutrals-350);
        letter-spacing: var(--il-font-letter-spacing-2);
        text-align: left;
      }

      .il-dropdown-header__label--truncate {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .il-dropdown-header__right {
        display: flex;
      }

      .il-dropdown-header__chevron {
        color: var(--il-color-neutrals-350);
        display: flex;
        align-items: center;
      }

      .il-dropdown-header__detail {
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-bold);
        color: var(--il-color-neutrals-300);
        background-color: var(--il-color-accent-50);
        border: 1px solid var(--il-color-accent-200);
        margin-top: 8px;
        border-radius: 4px;
        padding: 2px 6px;
        white-space: nowrap;
      }
    `,
  ] as CSSResultGroup;
}

declare global {
  interface HTMLElementTagNameMap {
    'il-dropdown-header': DropdownHeaderComponent;
  }
}
